import FilterDate from '@/Enums/FilterDate.enum';
import { IFilterFormData } from '@/Interfaces/PdfBuilder/FilterFormData.interface';
import PlatformValues from '@/Interfaces/PdfBuilder/FilterValues/PlatformValues.enum';
import type { IPdfCustomReportTemplateOption } from '@/Interfaces/PdfBuilder/Template.interface';
import ClearAllButton from '@/components/Buttons/ClearAllButton';
import DateRangeWithShortcuts from '@/components/DateRangeWithShortcuts';
import FiltersBadge from '@/components/Filter/Badge';
import FormErrorHelperText from '@/components/Form/FormErrorHelperText';
import BrowserIcon from '@/components/Icons/BrowserIcon';
import MegaphoneIcon from '@/components/Icons/MegaphoneIcon';
import SearchListIcon from '@/components/Icons/SearchListIcon';
import StatusUp from '@/components/Icons/StatusUp';
import ColumnSelect from '@/components/Select/ColumnSelect';
import { attributionReportOptions, deliveryReportOptions, platformOptions } from '@/constants/pdfBuilder/filterOptions';
import { getShortcutItemsInclusive } from '@/helpers/datePicker/shortcuts';
import { getDefaultFilters } from '@/helpers/pdfBuilder';
import FilterPopover from '@/pages/PdfBuilder/Filter/components/FilterPopover';
import { ArrowDropDown, SaveOutlined, TableRowsOutlined, Web } from '@mui/icons-material';
import { Autocomplete, Button, TextField, autocompleteClasses, inputBaseClasses } from '@mui/material';
import Paper from '@mui/material/Paper';
import type { DateRange } from '@mui/x-date-pickers-pro';
import type { Moment } from 'moment/moment';
import { useCallback, useEffect, useMemo } from 'react';
import { Controller, SubmitHandler, useFormContext } from 'react-hook-form';

interface IProps {
  templates: IPdfCustomReportTemplateOption[];
  loadingTemplates: boolean;
  loadTemplateFilters: (id: number | undefined) => Promise<void>;
  onSaveAsTemplateClick: () => void;
  onSubmit?: (data: IFilterFormData) => Promise<void>;
  onScheduleReportClick?: () => void;
}

const Filter = ({
  templates,
  loadingTemplates,
  loadTemplateFilters,
  onSaveAsTemplateClick,
  onSubmit,
  onScheduleReportClick,
}: IProps) => {
  const { watch, control, reset, setValue, handleSubmit, formState, trigger } = useFormContext<IFilterFormData>();

  useEffect(() => {
    const subscription = watch((value, { name }) => {
      if (name === 'template') {
        loadTemplateFilters(value.template?.id);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, loadTemplateFilters]);

  const [
    dateRange,
    dateRangeFormat,
    platforms,
    filteredWhitelabels,
    filteredClients,
    filteredAdvertisers,
    deliveryReports,
    attributionReports,
  ] = watch([
    'dateRange',
    'dateRangeFormat',
    'platforms',
    'whitelabels',
    'clients',
    'advertisers',
    'deliveryReports',
    'attributionReports',
  ]);

  const filterCount = useMemo(
    () =>
      [
        platforms?.length ? 1 : 0,
        dateRange?.length ? 1 : 0,
        filteredWhitelabels?.length ? 1 : 0,
        filteredClients?.length ? 1 : 0,
        filteredAdvertisers?.length ? 1 : 0,
      ].reduce((sum, curr) => sum + curr, 0),
    [
      platforms?.length,
      dateRange?.length,
      filteredWhitelabels?.length,
      filteredClients?.length,
      filteredAdvertisers?.length,
    ],
  );

  useEffect(() => {
    trigger(['platforms', 'deliveryReports']);
  }, [platforms?.length, deliveryReports?.length, trigger]);

  const generateReportDisabled = useMemo(() => {
    if (!formState.isValid) return true;
    const hasPlatformAndDelivery = !!platforms?.length && !!deliveryReports?.length;
    const hasAttribution = !!attributionReports?.length;
    return !(hasPlatformAndDelivery || hasAttribution);
  }, [formState.isValid, platforms?.length, deliveryReports?.length, attributionReports?.length]);

  const scheduleReportsDisabled = useMemo(
    () => generateReportDisabled || dateRangeFormat === FilterDate.CUSTOM_RANGE,
    [generateReportDisabled, dateRangeFormat],
  );

  const onDateRangeChange = useCallback(
    (value: DateRange<Moment>, format?: FilterDate) => {
      setValue('dateRange', value);
      if (format) {
        setValue('dateRangeFormat', format);
      }
    },
    [setValue],
  );

  const onReset = useCallback(() => {
    reset(getDefaultFilters());
  }, [reset]);

  const onFormSubmit: SubmitHandler<IFilterFormData> = async formData => {
    await onSubmit?.(formData);
  };

  return (
    <form onSubmit={handleSubmit(onFormSubmit)}>
      <Paper className='py-[24px] px-[30px] flex flex-col gap-y-[16px]'>
        <div className='flex items-center justify-between'>
          <div className='flex gap-[9px] items-center'>
            <span className='text-[24px] font-bold'>Filter</span>
            <FiltersBadge
              count={filterCount}
              sx={{ minWidth: '26px', width: 'unset', height: '26px', fontSize: '14px' }}
            />
            <ClearAllButton onClick={onReset} type='button' />
          </div>
          <div className='space-x-[9px]'>
            <Button variant='contained' startIcon={<SearchListIcon />} type='submit' disabled={generateReportDisabled}>
              Generate Report
            </Button>
            <Button
              variant='outlined'
              startIcon={<SaveOutlined />}
              type='button'
              onClick={onSaveAsTemplateClick}
              disabled={generateReportDisabled}
            >
              Save as Template
            </Button>
            <Button
              variant='outlined'
              startIcon={<TableRowsOutlined width='16' />}
              type='button'
              onClick={onScheduleReportClick}
              disabled={scheduleReportsDisabled}
            >
              Schedule Report
            </Button>
          </div>
        </div>
        <div className='flex flex-wrap gap-[16px] items-center'>
          <div className='flex-[2] basis-[200px]'>
            <Controller
              control={control}
              name='dateRange'
              rules={{
                required: {
                  message: 'Date range is required.',
                  value: true,
                },
              }}
              render={({ field: { ...field }, fieldState: { error } }) => (
                <>
                  {error ? (
                    <FormErrorHelperText sx={{ position: 'absolute', top: '-25px', left: '14px' }}>
                      {error?.message}
                    </FormErrorHelperText>
                  ) : null}
                  <DateRangeWithShortcuts
                    {...field}
                    fullWidth
                    disableFuture
                    dateRangeFormat={dateRangeFormat}
                    onChange={onDateRangeChange}
                    shortcuts={getShortcutItemsInclusive()}
                  />
                </>
              )}
            />
          </div>
          <div className='flex-[2] basis-[200px]'>
            <Controller
              control={control}
              name='platforms'
              rules={{
                validate: (value, formValues) =>
                  !formValues.deliveryReports?.length || !!value?.length || 'Select at least one platform',
              }}
              render={({ field, fieldState: { error } }) => (
                <div className='flex-1'>
                  {error ? (
                    <FormErrorHelperText sx={{ position: 'absolute', top: '-25px', left: '14px' }}>
                      {error.message}
                    </FormErrorHelperText>
                  ) : null}
                  <ColumnSelect
                    {...field}
                    value={field.value ?? []}
                    id='column-select-platforms'
                    options={platformOptions}
                    startAdornment={<MegaphoneIcon />}
                    fullWidth
                    placeholder='Platform'
                    label='Platform'
                    error={!!error}
                  />
                </div>
              )}
            />
          </div>
          <Controller
            control={control}
            name='template'
            render={({ field }) => (
              <Autocomplete
                {...field}
                value={field.value ?? null}
                id='template-select'
                sx={{ flex: 2, flexBasis: '200px' }}
                options={templates}
                fullWidth
                disabled={loadingTemplates}
                onChange={(_, data) => field.onChange(data)}
                getOptionLabel={option => option?.name || ''}
                isOptionEqualToValue={(option, selectValue) => !!selectValue && option.id === selectValue?.id}
                renderInput={params => (
                  <TextField
                    {...params}
                    placeholder='Template'
                    variant='outlined'
                    sx={{
                      [`& .${inputBaseClasses.root}.${inputBaseClasses.adornedEnd}`]: {
                        padding: '0 14px',
                      },
                      [`& .${inputBaseClasses.input}.${autocompleteClasses.input}`]: {
                        padding: '12px 16px 12px 8px',
                      },
                    }}
                    slotProps={{
                      input: {
                        ...params.InputProps,
                        startAdornment: <Web />,
                        endAdornment: <ArrowDropDown />,
                      },
                    }}
                  />
                )}
              />
            )}
          />
        </div>
        <div className='flex flex-wrap gap-[16px] items-center'>
          <div className='flex-[2] basis-[200px]'>
            <Controller
              control={control}
              name='deliveryReports'
              rules={{
                validate: (value, formValues) =>
                  !formValues.platforms?.length || !!value?.length || 'Select at least one Reporting Dashboard',
              }}
              render={({ field, fieldState: { error } }) => (
                <div className='flex-1'>
                  {error ? (
                    <FormErrorHelperText sx={{ position: 'absolute', top: '-25px', left: '14px' }}>
                      {error.message}
                    </FormErrorHelperText>
                  ) : null}
                  <ColumnSelect
                    {...field}
                    value={field.value ?? []}
                    id='column-select-reporting-dashboard'
                    options={deliveryReportOptions}
                    grouped
                    startAdornment={<StatusUp />}
                    fullWidth
                    placeholder='Reporting Dashboard'
                    label='Reporting Dashboard'
                    error={!!error}
                  />
                </div>
              )}
            />
          </div>
          <div className='flex-[2] basis-[200px]'>
            <Controller
              control={control}
              name='attributionReports'
              render={({ field, fieldState: { error } }) => (
                <div className='flex-1'>
                  {error ? (
                    <FormErrorHelperText sx={{ position: 'absolute', top: '-25px', left: '14px' }}>
                      {error.message}
                    </FormErrorHelperText>
                  ) : null}
                  <ColumnSelect
                    {...field}
                    value={field.value ?? []}
                    id='column-select-attribution'
                    options={attributionReportOptions}
                    grouped
                    startAdornment={<BrowserIcon />}
                    fullWidth
                    placeholder='Attribution'
                    label='Attribution'
                    error={!!error}
                  />
                </div>
              )}
            />
          </div>
        </div>
        <div className='flex gap-[6px] flex-wrap items-center justify-start'>
          <FilterPopover
            filters={{
              whitelabels: filteredWhitelabels,
              clients: filteredClients,
              advertisers: filteredAdvertisers,
            }}
            platforms={platforms?.map(option => option.value as PlatformValues)}
          />
        </div>
      </Paper>
    </form>
  );
};

export default Filter;
